import React, {
  useState,
  useEffect,
  Suspense,
  useRef,
  useCallback,
} from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Maximize, Minimize, Bug } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import DarkModeToggle from './components/DarkModeToggle';
import MusicToggle from './components/MusicToggle';
import SoundToggle from './components/SoundToggle';
import Footer from './components/Footer';
import { getGameByPath, getNextGame, getPreviousGame } from './games/registry';
import BackgroundTexture from './components/BackgroundTexture';
import GameMenu, { GameMenuHandles } from './components/GameMenu';
import GameContainer from './components/GameContainer';
import CookieConsent from './components/CookieConsent';
import Tooltip from './components/Tooltip';
import titleUtils from './utils/titleUtils';
import {
  setNavigatingBetweenGames as setNavigatingBetweenGamesUtil,
  playBackgroundMusic,
  playPressSound,
  pauseBackgroundMusic,
  setNavigatingBetweenGames,
  stopBackgroundMusic,
  fadeOutAndStopBackgroundMusic,
  soundManager,
  setIsInGamePage,
  setOpenGameMenuCallback,
  playPressSoundDirectly,
} from './utils/soundUtils';

interface AnimationDelays {
  windowTransition: number;
}

const GamePage: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isGameReady, setIsGameReady] = useState(false);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [hoverSide, setHoverSide] = useState<'left' | 'right' | 'none'>('none');
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [isTitleVisible, setIsTitleVisible] = useState(true);
  const [currentRound, setCurrentRound] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [roundProgress, setRoundProgress] = useState(0);
  const [isNavigationAllowed, setIsNavigationAllowed] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [animationDelays, setAnimationDelays] = useState<AnimationDelays>({
    windowTransition: 500,
  });
  const [hasLost, setHasLost] = useState(false);
  const [isAbandoned, setIsAbandoned] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [reset, setReset] = useState(0);
  const [currentGameStats, setCurrentGameStats] = useState<{
    customStat: number;
  }>({ customStat: 0 });
  const [sessionStartBest, setSessionStartBest] = useState<number>(0);
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [currentGameMode, setCurrentGameMode] = useState('');
  const [isPausedByExpand, setIsPausedByExpand] = useState(false);

  const { path } = useParams<{ path: string }>();
  const navigate = useNavigate();

  const game = path ? getGameByPath(`/${path}`) : null;
  const GameComponent = game?.component;

  // Redirigir a la página principal si el juego no existe
  useEffect(() => {
    if (path && !game) {
      navigate('/', { replace: true });
    }
  }, [path, game, navigate]);

  // Cargar el último modo de juego guardado cuando se carga un juego
  useEffect(() => {
    if (game) {
      const lastGameMode = game.getLastGameMode();
      if (lastGameMode) {
        setCurrentGameMode(lastGameMode);
        setReset((prev) => prev + 1);
      } else if (game.info.defaultGameMode) {
        setCurrentGameMode(game.info.defaultGameMode);
        setReset((prev) => prev + 1);
      }
    }
  }, [game]);

  const nextGame = game ? getNextGame(game.info.id) : null;
  const previousGame = game ? getPreviousGame(game.info.id) : null;
  const isFirstLoad = useRef(true);
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const navigationDirection = useRef<'left' | 'right'>('right');
  const { titleFontSize } = titleUtils();

  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const fullscreenRef = useRef<HTMLDivElement>(null);
  const expandAnimationRef = useRef<ReturnType<
    typeof requestAnimationFrame
  > | null>(null);

  const gameMenuRef = useRef<GameMenuHandles | null>(null);

  // Añadir una referencia al botón de ampliar/desampliar
  const expandButtonRef = useRef<HTMLButtonElement>(null);

  const handleResize = useCallback(() => {
    setWindowHeight(window.innerHeight);
    setIsMobile(window.innerWidth <= 640);
  }, []);

  const handleGlobalMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isMobile) return;
      const threshold = 0.3;
      const leftThreshold = window.innerWidth * threshold;
      const rightThreshold = window.innerWidth * (1 - threshold);

      if (e.clientX < leftThreshold) {
        setHoverSide('left');
      } else if (e.clientX > rightThreshold) {
        setHoverSide('right');
      } else {
        setHoverSide('none');
      }
    },
    [isMobile]
  );

  const handleComponentLoad = useCallback(() => {
    setIsLoaded(true);
    setTimeout(() => {
      setIsGameReady(true);
    }, 700);
    isFirstLoad.current = false;
  }, []);

  // Referencia para mantener el estado del juego durante la ampliación/desampliación
  const gameStateRef = useRef({
    isLoaded: false,
    isGameReady: false,
    isExpanding: false,
  });

  useEffect(() => {
    // Si estamos ampliando/desampliando, guardar el estado actual
    if (gameStateRef.current.isLoaded && gameStateRef.current.isGameReady) {
      gameStateRef.current.isExpanding = true;
    }

    // Solo ejecutar este efecto cuando cambia path, GameComponent o game
    // No cuando cambia isExpanded
    if (!gameStateRef.current.isExpanding) {
      setIsLoaded(false);
      setIsGameReady(false);
      setIsTitleVisible(!isExpanded);
      setCurrentRound(1);
      setIsPaused(false);
      setRoundProgress(0);
      setIsNavigationAllowed(true);
      setHasLost(false);
      setIsAbandoned(false);
      setReset(0);
      setCurrentGameStats({ customStat: 0 });

      // Actualizar el estado isInGamePage cuando el componente se monta
      setIsInGamePage(true);

      // Verificar si la página fue recargada
      const wasReloaded = sessionStorage.getItem('gameWasReloaded') === 'true';
      if (wasReloaded) {
        // Eliminar inmediatamente para evitar múltiples aperturas
        sessionStorage.removeItem('gameWasReloaded');
        // Marcar para abrir el menú después de que el juego esté listo
        sessionStorage.setItem('openGameMenuAfterLoad', 'true');
      }

      // Configurar el evento beforeunload para detectar recargas
      const handleBeforeUnload = () => {
        // Solo guardar el indicador si estamos en una página de juego
        if (path && path !== '/') {
          sessionStorage.setItem('gameWasReloaded', 'true');
          sessionStorage.setItem('lastGamePath', path);
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      if (game?.getStats) {
        const statsAtStart = game.getStats();
        const bestAtStart = statsAtStart?.maxRound || 0;
        setSessionStartBest(bestAtStart);
      }

      if (GameComponent) {
        handleComponentLoad();
      }

      window.addEventListener('resize', handleResize);
      window.addEventListener('mousemove', handleGlobalMouseMove);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('beforeunload', handleBeforeUnload);

        // Actualizar el estado isInGamePage cuando el componente se desmonta
        setIsInGamePage(false);
      };
    } else {
      // Restablecer la bandera de expansión
      gameStateRef.current.isExpanding = false;
    }
  }, [
    path,
    GameComponent,
    handleComponentLoad,
    handleGlobalMouseMove,
    handleResize,
    game,
  ]);

  // Efecto para actualizar la referencia del estado del juego
  useEffect(() => {
    gameStateRef.current.isLoaded = isLoaded;
    gameStateRef.current.isGameReady = isGameReady;
  }, [isLoaded, isGameReady]);

  // Efecto separado para manejar los cambios de isExpanded
  useEffect(() => {
    // Solo actualizar la visibilidad del título cuando cambia isExpanded
    setIsTitleVisible(!isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    if (isLoaded && isGameReady) {
      setIsGameStarted(true);

      // Verificar si debemos abrir el menú después de cargar
      const shouldOpenMenu =
        sessionStorage.getItem('openGameMenuAfterLoad') === 'true';

      // Verificar que estamos en la misma página que se recargó
      const lastGamePath = sessionStorage.getItem('lastGamePath');
      const shouldOpenInThisGame = !lastGamePath || lastGamePath === path;

      if (shouldOpenMenu && shouldOpenInThisGame && gameMenuRef.current) {
        // Eliminar inmediatamente para evitar múltiples aperturas
        sessionStorage.removeItem('openGameMenuAfterLoad');
        sessionStorage.removeItem('lastGamePath');

        // Usar un pequeño retraso para asegurar que todo esté listo
        setTimeout(() => {
          if (gameMenuRef.current && !isMenuOpen) {
            gameMenuRef.current.pauseAndOpenMenu();
            setIsMenuOpen(true);
            setIsPaused(true);
          }
        }, 500);
      }
    }
  }, [isLoaded, isGameReady, game, path, isMenuOpen]);

  // Configurar el callback para abrir el menú del juego cuando se cambia de pestaña o se recarga la página
  useEffect(() => {
    if (gameMenuRef.current) {
      // Configurar el callback para abrir el menú del juego
      setOpenGameMenuCallback(() => {
        if (!isMenuOpen && gameMenuRef.current) {
          gameMenuRef.current.pauseAndOpenMenu();
        }
      });
    }

    // Añadir event listener para detectar cuando la pestaña pierde el foco
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'hidden' &&
        gameMenuRef.current &&
        !isMenuOpen
      ) {
        // La pestaña ha perdido el foco, pausar el juego y abrir el menú
        gameMenuRef.current.pauseAndOpenMenu();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Limpiar el callback cuando el componente se desmonta
      setOpenGameMenuCallback(() => {});
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isMenuOpen, gameMenuRef]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart || hasLost) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (isMenuOpen) {
      if (Math.abs(diff) > 50) {
        if (diff > 0 && nextGame && isNavigationAllowed) {
          navigationDirection.current = isMobile ? 'left' : 'right';
          handleNavigation(nextGame.info.path);
        } else if (diff < 0 && previousGame && isNavigationAllowed) {
          navigationDirection.current = isMobile ? 'right' : 'left';
          handleNavigation(previousGame.info.path);
        }
      }
    }
    setTouchStart(null);
  };

  const handleNavigation = useCallback(
    (newPath: string) => {
      if (!isNavigationAllowed) return;

      setNavigatingBetweenGamesUtil(true);

      setIsTitleVisible(false);
      setIsGameReady(false);
      setIsMenuOpen(false);
      setRoundProgress(0);
      setIsGameStarted(false);

      const direction: 'left' | 'right' = navigationDirection.current;

      if (gameContainerRef.current) {
        gameContainerRef.current.style.transition = `transform ${
          animationDelays.windowTransition / 1000
        }s ease-in-out, opacity ${
          animationDelays.windowTransition / 1000
        }s ease-in-out`;
        gameContainerRef.current.style.transform =
          direction === 'left'
            ? 'translateX(-100%) scale(1.02)'
            : 'translateX(100%) scale(1.02)';
        gameContainerRef.current.style.opacity = '0';
      }

      setTimeout(() => {
        navigate(newPath);

        if (gameContainerRef.current) {
          gameContainerRef.current.style.transition = 'none';
          gameContainerRef.current.style.transform =
            direction === 'left'
              ? `translateX(100%) scale(${isExpanded ? 1.02 : 1})`
              : `translateX(-100%) scale(${isExpanded ? 1.02 : 1})`;
          gameContainerRef.current.style.opacity = '0';

          requestAnimationFrame(() => {
            if (gameContainerRef.current) {
              gameContainerRef.current.style.transition = `transform ${
                animationDelays.windowTransition / 1000
              }s ease-in-out, opacity ${
                animationDelays.windowTransition / 1000
              }s ease-in-out`;
              gameContainerRef.current.style.transform = `translateX(0) scale(${
                isExpanded ? 1.02 : 1
              })`;
              gameContainerRef.current.style.opacity = '1';
            }
          });
        }

        setTimeout(() => {
          setIsTitleVisible(true);
          setIsMenuOpen(true);
          setIsGameStarted(true);

          setTimeout(() => {
            setNavigatingBetweenGamesUtil(false);
          }, 500);
        }, animationDelays.windowTransition);
      }, animationDelays.windowTransition);
    },
    [navigate, isNavigationAllowed, animationDelays, isExpanded]
  );

  const handleNextRound = () => {
    if (!isMenuOpen) {
      setCurrentRound((prevRound) => prevRound + 1);
    }
  };

  const handleGameEnd = (gameStats?: { customStat: number }) => {
    if (game && gameStats !== undefined) {
      const customStatValue =
        gameStats.customStat !== undefined ? gameStats.customStat : 0;
      setCurrentGameStats({ customStat: customStatValue });
    }
  };

  const handleTotalClicks = (clicks: number) => {};

  const handlePause = () => {
    setIsPaused(true);
  };

  const handleResume = () => {
    setIsPaused(false);
    setIsPausedByExpand(false);
  };

  const handleUpdateProgress = (progress: number) => {
    setRoundProgress(progress);
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handlePreviousGame = () => {
    if (previousGame) {
      navigationDirection.current = 'left';
      handleNavigation(previousGame.info.path);
    }
  };

  const handleNextGame = () => {
    if (nextGame) {
      navigationDirection.current = 'right';
      handleNavigation(nextGame.info.path);
    }
  };

  const handleGameLost = (
    lost: boolean,
    stats?: { customStat: number; currentClicks: number },
    isAbandoned?: boolean
  ) => {
    if (lost && game) {
      setHasLost(true);
      setIsPaused(true);

      const finalCustomStat =
        stats?.customStat !== undefined
          ? stats.customStat
          : currentGameStats.customStat;
      setCurrentGameStats({ customStat: finalCustomStat });

      if (isAbandoned) {
        setIsAbandoned(true);
        setIsGameReady(false);
      }

      const existingStats = game.getStats();
      const updatedStats = { ...existingStats };
      updatedStats.gamesPlayed = (updatedStats.gamesPlayed || 0) + 1;
      if (currentRound > (updatedStats.maxRound || 0)) {
        updatedStats.maxRound = currentRound;
      }

      updatedStats.customStat =
        (existingStats.customStat || 0) + finalCustomStat;

      if (game.saveStats) {
        game.saveStats(updatedStats);
      }

      setReset((prev) => prev + 1);
      gameMenuRef.current?.pauseAndOpenMenu();
    }
  };

  const handleRestart = () => {
    // Asegurarse de que no hay indicadores de recarga pendientes
    sessionStorage.removeItem('openGameMenuAfterLoad');
    sessionStorage.removeItem('gameWasReloaded');
    sessionStorage.removeItem('lastGamePath');

    setReset((prev) => prev + 1);
    setRoundProgress(0);
    setHasLost(false);
    setIsAbandoned(false);
    setIsMenuOpen(false);
    setIsPaused(false);
    setIsPausedByExpand(false);
    setIsNavigationAllowed(true);
    setIsGameReady(true);
    setCurrentRound(1);
    setCurrentGameStats({ customStat: 0 });

    if (game?.getStats) {
      const statsAtStart = game.getStats();
      const bestAtStart = statsAtStart?.maxRound || 0;
      setSessionStartBest(bestAtStart);
    }
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      if (fullscreenRef.current) {
        fullscreenRef.current
          .requestFullscreen()
          .then(() => setIsFullScreen(true))
          .catch((err) =>
            console.error('Error al intentar entrar en fullscreen:', err)
          );
      }
    } else if (document.exitFullscreen) {
      document
        .exitFullscreen()
        .then(() => setIsFullScreen(false))
        .catch((err) =>
          console.error('Error al intentar salir de fullscreen:', err)
        );
    }
  };

  const toggleExpand = () => {
    playPressSound();
    const wasExpanded = isExpanded;

    // Marcar que estamos en proceso de ampliación/desampliación
    gameStateRef.current.isExpanding = true;

    // Restablecer el estilo del botón para evitar que se quede grande
    // Usar un pequeño retraso para permitir que la animación de clic termine
    setTimeout(() => {
      if (expandButtonRef.current) {
        expandButtonRef.current.style.transform = 'scale(1)';

        // Eliminar el estilo inline después de un tiempo adicional
        // para permitir que el botón vuelva a animarse
        setTimeout(() => {
          if (expandButtonRef.current) {
            expandButtonRef.current.style.transform = '';
          }
        }, 100);
      }
    }, 300); // 300ms debería ser suficiente para que termine la animación de clic

    setIsExpanded(!wasExpanded);
    setIsTitleVisible(wasExpanded);

    // Si estamos desampliando (wasExpanded es true), reanudar el juego
    // Si estamos ampliando (wasExpanded es false), pausar el juego usando isPausedByExpand
    if (wasExpanded) {
      setIsPausedByExpand(false);
      handleResume();
    } else {
      setIsPausedByExpand(true);
      // Solo pausar si no estaba ya pausado
      if (!isPaused) {
        handlePause();
      }
    }

    if (headerRef.current && footerRef.current && gameContainerRef.current) {
      const transitionTime = `${animationDelays.windowTransition / 1000}s`;
      headerRef.current.style.transition = `opacity ${transitionTime}, transform ${transitionTime}`;
      footerRef.current.style.transition = `opacity ${transitionTime}, transform ${transitionTime}`;
      gameContainerRef.current.style.transition = `transform ${transitionTime} cubic-bezier(0.4, 0, 0.2, 1), height ${transitionTime} cubic-bezier(0.4, 0, 0.2, 1)`;

      const frame = () => {
        if (gameContainerRef.current) {
          const headerTransform = wasExpanded
            ? 'translateY(0)'
            : 'translateY(-100px)';
          const headerOpacity = wasExpanded ? '1' : '0';
          const footerTransform = wasExpanded
            ? 'translateY(0)'
            : 'translateY(100px)';
          const footerOpacity = wasExpanded ? '1' : '0';
          const gameScale = wasExpanded ? 'scale(1)' : 'scale(1.02)';
          const gameHeight = wasExpanded ? `${windowHeight}px` : `100vh`;

          headerRef.current.style.transform = headerTransform;
          headerRef.current.style.opacity = headerOpacity;
          footerRef.current.style.transform = footerTransform;
          footerRef.current.style.opacity = footerOpacity;
          gameContainerRef.current.style.transform = gameScale;
          gameContainerRef.current.style.height = gameHeight;
        }
      };

      // Cancelar cualquier animación anterior
      if (expandAnimationRef.current) {
        cancelAnimationFrame(expandAnimationRef.current);
      }
      expandAnimationRef.current = requestAnimationFrame(frame);

      // Restablecer el estado isExpanding después de la animación
      setTimeout(() => {
        gameStateRef.current.isExpanding = false;
      }, animationDelays.windowTransition);
    }

    // Eliminar la apertura automática del menú al ampliar/desampliar
  };

  const navigateBack = () => {
    navigate('/');
  };

  const handleGameModeChange = (mode: string) => {
    setCurrentGameMode(mode);
    // Guardar el último modo de juego utilizado
    if (game) {
      game.saveLastGameMode(mode);
    }
  };

  return (
    <div className="relative z-0 h-screen overflow-hidden" ref={fullscreenRef}>
      <BackgroundTexture
        className="background-texture"
        zIndex={-1}
        patternType="mixed"
      />
      <div className="h-full flex flex-col">
        <div
          className={`flex-grow px-2 sm:px-6 lg:px-8 py-2 sm:py-8 ${
            isMobile ? 'overflow-auto' : 'overflow-hidden'
          }`}
        >
          <div
            className={`h-full flex flex-col ${
              isExpanded ? '' : 'max-w-7xl mx-auto'
            }`}
          >
            <header
              ref={headerRef}
              className={`flex justify-between items-center mb-2 sm:mb-8 transition-all duration-300 ${
                isLoaded
                  ? 'translate-y-0 opacity-100'
                  : '-translate-y-10 opacity-0'
              } ${isExpanded ? 'hidden' : ''}`}
            >
              <div className="flex items-center">
                <Link to="/" className="group">
                  <div className="icon-bg">
                    <ArrowLeft className="text-white w-1/2 h-1/2" />
                  </div>
                </Link>
                <div className="ml-2 hidden sm:flex sm:items-center audio-controls-container">
                  <MusicToggle />
                </div>
              </div>
              <h1
                className={`font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-red-500 absolute left-1/2 transform -translate-x-1/2 transition-opacity duration-300 whitespace-nowrap ${
                  isTitleVisible && isLoaded ? 'opacity-100' : 'opacity-0'
                } ${
                  isMobile && game?.info.title.length > 10
                    ? 'text-3xl'
                    : titleFontSize
                }`}
              >
                {game?.info.title}
              </h1>
              <div className="flex items-center">
                <div className="flex items-center sm:hidden audio-controls-container">
                  {/* Eliminamos el botón de música en móviles en esta posición */}
                </div>
                <div className="hidden sm:flex sm:items-center ml-2 audio-controls-container">
                  <SoundToggle />
                </div>
                <div className="hidden sm:flex sm:items-center ml-2 audio-controls-container">
                  <DarkModeToggle
                    onClick={() => {
                      playPressSoundDirectly();
                    }}
                  />
                </div>
                {/* Añadimos el botón de música a la derecha solo en móviles */}
                <div className="flex items-center sm:hidden audio-controls-container">
                  <MusicToggle />
                </div>
              </div>
            </header>

            <div
              ref={gameContainerRef}
              className={`relative bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden transition-all duration-300 ease-out ${
                isLoaded
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-4 opacity-0'
              } ${isGameReady ? 'fade-in' : ''}`}
              style={{
                height: isExpanded ? '100vh' : `${windowHeight}px`,
                minHeight: isExpanded ? undefined : '400px',
                transition:
                  'height 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s ease-out, opacity 0.3s ease-out',
              }}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {!isMobile && (
                <motion.div
                  className="absolute top-0 left-0 z-50 pointer-events-none flex items-center"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6, duration: 0.3 }}
                >
                  <div className="relative pointer-events-auto">
                    <Tooltip content="Reportar Error" position="right">
                      <div className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white py-2 px-3 rounded-b-md shadow-md inline-flex items-center game-menu-container transition-transform duration-500 transform -ml-1">
                        <motion.a
                          href={`https://x.com/messages/compose?recipient_id=1849015375087738880&text=Hola, en el minijoc ${game?.info.title} `}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 dark:text-gray-400 p-2 rounded-full hover:bg-transparent dark:hover:bg-transparent transition-colors active:scale-95 transition-transform duration-100 hover:scale-125"
                          whileTap={{ scale: 0.9 }}
                          whileHover={{ scale: 1.2 }}
                          onClick={(e) => {
                            playPressSound();
                            e.preventDefault();
                            gameMenuRef.current?.pauseAndOpenMenu();
                            window.open(e.currentTarget.href, '_blank');
                          }}
                        >
                          <Bug className="w-5 h-5" />
                        </motion.a>
                      </div>
                    </Tooltip>
                  </div>
                </motion.div>
              )}

              {!isMobile && (
                <motion.div
                  className="absolute top-0 right-0 z-50 pointer-events-none flex items-center"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6, duration: 0.3 }}
                >
                  <div className="relative pointer-events-auto">
                    <Tooltip
                      content={isExpanded ? 'Desampliar' : 'Ampliar'}
                      position="left"
                    >
                      <div className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white py-2 px-3 rounded-b-md shadow-md inline-flex items-center game-menu-container transition-transform duration-500 transform -mr-1">
                        <motion.button
                          ref={expandButtonRef}
                          onClick={toggleExpand}
                          className="text-gray-500 dark:text-gray-400 p-2 rounded-full hover:bg-transparent dark:hover:bg-transparent transition-colors active:scale-95 transition-transform duration-100 hover:scale-125"
                          whileTap={{ scale: 0.9 }}
                          whileHover={{ scale: 1.2 }}
                        >
                          {isExpanded ? (
                            <Minimize className="w-5 h-5" />
                          ) : (
                            <Maximize className="w-5 h-5" />
                          )}
                        </motion.button>
                      </div>
                    </Tooltip>
                  </div>
                </motion.div>
              )}

              <motion.div
                className="z-50 pointer-events-none"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                <div className="pointer-events-auto">
                  <GameMenu
                    ref={gameMenuRef}
                    currentRound={currentRound}
                    onPause={handlePause}
                    onResume={handleResume}
                    onRestart={handleRestart}
                    gameInstructions={game?.info.instructions || []}
                    roundProgress={roundProgress}
                    onNavigationChange={setIsNavigationAllowed}
                    hasPreviousGame={!!previousGame}
                    hasNextGame={!!nextGame}
                    onPrevious={handlePreviousGame}
                    onNext={handleNextGame}
                    isMenuOpen={isMenuOpen}
                    onMenuToggle={handleMenuToggle}
                    isMenuVisible={isMenuOpen}
                    hasLost={hasLost}
                    onExit={() => navigate('/')}
                    gameName={game?.info.title || ''}
                    game={game}
                    resetCounter={reset}
                    currentGameStats={currentGameStats}
                    sessionStartBest={sessionStartBest}
                    onNavigateBack={navigateBack}
                    isExpanded={isExpanded}
                    onToggleExpand={toggleExpand}
                    onRoundProgressChange={setRoundProgress}
                    onGameLost={handleGameLost}
                    setHasLost={setHasLost}
                    onGameModeChange={handleGameModeChange}
                    currentGameMode={currentGameMode}
                  />
                </div>
              </motion.div>

              {!isGameReady ? (
                <motion.div
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div
                    className="animate-spin rounded-full border-b-8 border-yellow-500"
                    style={{
                      width: 'min(40vw, 40vh)',
                      height: 'min(40vw, 40vh)',
                      maxWidth: '200px',
                      maxHeight: '200px',
                      minWidth: '64px',
                      minHeight: '64px',
                    }}
                  ></div>
                </motion.div>
              ) : (
                <GameContainer
                  isMenuOpen={isMenuOpen}
                  hasLost={hasLost}
                  isAbandoned={isAbandoned}
                >
                  {GameComponent && (
                    <Suspense
                      fallback={
                        <motion.div
                          className="absolute inset-0 flex items-center justify-center pointer-events-none"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div
                            className="animate-spin rounded-full border-b-8 border-yellow-500"
                            style={{
                              width: 'min(40vw, 40vh)',
                              height: 'min(40vw, 40vh)',
                              maxWidth: '200px',
                              maxHeight: '200px',
                              minWidth: '64px',
                              minHeight: '64px',
                            }}
                          ></div>
                        </motion.div>
                      }
                    >
                      <GameComponent
                        handleNextRound={handleNextRound}
                        handleGameEnd={handleGameEnd}
                        handleTotalClicks={handleTotalClicks}
                        isPaused={isPaused && !isPausedByExpand}
                        updateProgress={handleUpdateProgress}
                        onMenuToggle={handleMenuToggle}
                        onGameLost={handleGameLost}
                        isMenuOpen={isMenuOpen}
                        reset={reset}
                        gameMode={currentGameMode}
                      />
                    </Suspense>
                  )}
                </GameContainer>
              )}
            </div>
          </div>
        </div>
        <div
          ref={footerRef}
          className={`transition-all duration-300 ${
            isExpanded || isMobile
              ? 'opacity-0 transform translate-y-10 hidden'
              : 'opacity-100 transform translate-y-0'
          }`}
        >
          {!isExpanded && !isMobile && <Footer />}
        </div>
      </div>
      <CookieConsent />
    </div>
  );
};

export default GamePage;
